import Layout from "../../components/reusable/Layout";
import logo from "../../images/syncLife/syncLifeLogo.png";
import testingTrackingIcon from "../../images/syncLife/testingTracking.png";
import targetedTherapiesIcon from "../../images/syncLife/targetedTherapies.png";
import lifestyleTechIcon from "../../images/syncLife/lifestyleNutrition.png";
import styled from "styled-components";

const BRAND_COLOR = "#144F56";

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledTitle = styled.div`
  text-align: center;
  font-size: 32px;
  font-family: "AktivGroteskEx_A_Md", sans-serif;
  line-height: 1.3;
  color: ${BRAND_COLOR};
  margin: -24px 40px 0;
`;

const IntroSection = styled.div`
  margin: 32px 40px 56px;
  padding: 24px;
  border-radius: 35px;
  background-color: var(--primary50);
`;

const IntroParagraph = styled.p`
  font-family: "AktivGroteskEx_A_Md", sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--primary800);
  margin: 0 0 15px 0;
`;

const ColumnsContainer = styled.div`
  display: flex;
  gap: 30px;
  margin: 0 40px;
`;

const Column = styled.div`
  border-radius: 35px;
  background-color: var(--primary50);
  padding: 16px 8px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
  padding: 2px;
  height: 65px;
`;

const ColumnTitle = styled.h2`
  font-family: "AktivGroteskEx_A_Rg", sans-serif;
  font-size: 16px;
  color: ${BRAND_COLOR};
  margin: 0;
  line-height: 1.3;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BulletItem = styled.li`
  font-family: "AktivGroteskEx_A_Rg", sans-serif;
  font-size: 10px;
  line-height: 1.9;
  color: var(--primary800);
  margin-bottom: 16px;
  position: relative;
  padding-left: 8px;

  &:before {
    content: "•";
    position: absolute;
    left: 0;
    color: ${BRAND_COLOR};
    font-weight: bold;
  }
`;

interface SyncLifePageData {
  title: string;
  introText: string[];
  columns: {
    title: string;
    items: string[];
  }[];
}

// Data structure for different metric types
export const syncLifePageData: Record<number, SyncLifePageData> = {
  25: {
    title: "Protocols for Stress Resiliency",
    introText: [
      "Your PNOĒ results show how your body is responding to stress today. At SYNC, we turn those insights into action with a personalized Stress Resiliency Protocol designed to calm the nervous system, restore recovery, and build lasting resilience.",
      "Here's how we translate your results into a personalized plan for stronger stress balance and recovery:",
    ],
    columns: [
      {
        title: "Testing & Tracking",
        items: [
          "Stress hormone and recovery rhythm insights",
          "Testing how stress is affecting your hormones, recovery, and long-term health",
          "HRV and nervous system balance evaluation",
        ],
      },
      {
        title: "Targeted Therapies",
        items: [
          "Customized supplements plans for adrenal and calming support",
          "Personalized peptide and hormone support for resilience",
          "Restorative nutrient IV therapies",
          "In-clinic sessions with guided biofeedback and BrainTap, plus red light therapy",
        ],
      },
      {
        title: "Lifestyle, \nTech & Nutrition Integrations",
        items: [
          "Breathing exercises and mindfulness to lower daily stress",
          "Evening wind-down rituals and recovery practices",
          "Nutrition strategies that support nervous system balance",
          "Wearables and biofeedback tools to monitor HRV and recovery trends",
          "Optional tech add-ons for stress regulation on-the-go",
          "Wellness tools that blend PEMF and red light therapy to reduce stress, improve HRV, and promote deeper recovery",
        ],
      },
    ],
  },
  4: {
    title: "Protocols for Heart Fitness & Resiliency",
    introText: [
      "Your PNOĒ results show how your body is performing today. At SYNC, we turn those insights into action with a personalized Heart Fitness Protocol designed to strengthen performance now and protect cardiovascular health for the future.",
      "Here's how we translate your results into a personalized plan for stronger, more resilient heart health:",
    ],
    columns: [
      {
        title: "Testing & Tracking",
        items: [
          "Heart health insights that go beyond the basics",
          "Assessing how stress and recovery affect your heart",
          "Looking at hormone balance, cholesterol balance, hidden inflammation and nutrients that support heart strength",
          "Evaluating how well your heart and blood vessels are aging",
        ],
      },
      {
        title: "Targeted Therapies",
        items: [
          "Customized supplement plans for heart and circulation",
          "Personalized peptide and hormone support to strengthen recovery and resilience",
          "Stress-balancing protocols",
          "Restorative nutrient IV & injection therapies",
        ],
      },
      {
        title: "Lifestyle, \nTech & Nutrition Integrations",
        items: [
          "Exploring simple shifts in exercise and breathing to balance heart rhythm",
          "Nutrition strategies to strengthen cardiovascular health",
          "Recovery practices such as sauna, contrast therapy, and relaxation breathing",
          "Heart-focused training like Zone 2 and Zone 4.",
          "Use of wearables & biofeedback tools to improve HRV and build stress resilience.",
        ],
      },
    ],
  },
  5: {
    title: "Protocols for Lung Fitness",
    introText: [
      "Your PNOĒ results highlight how your lungs are performing today. At SYNC, we turn those insights into action with a personalized Lung Fitness Protocol designed to improve oxygen use, reduce inflammation, and strengthen endurance.",
      "Here's how we translate your results into a personalized plan for stronger, more resilient lung health:",
    ],
    columns: [
      {
        title: "Testing & Tracking",
        items: [
          "Lung performance insights that go beyond basic testing",
          "Evaluating stress, inflammation, and oxygen transport",
          "Genetic and toxin testing to uncover hidden airway stressors",
          "Hormone and nutrient status to support lung efficiency",
        ],
      },
      {
        title: "Targeted Therapies",
        items: [
          "Customized supplement plans for lung antioxidants, detox pathways, and inflammation support",
          "Personalized peptide and hormone support to repair tissue and improve recovery",
          "Restorative nutrient IV therapies",
          "Targeted injections for oxygen use and energy production",
          "Weight management if needed",
        ],
      },
      {
        title: "Lifestyle, \nTech & Nutrition Integrations",
        items: [
          "Exploring simple shifts in breathwork (box breathing, pranayama, Wim Hof) to expand lung capacity",
          "Recovery and stress reduction practices like contrast therapy for oxygen utilization",
          "Wearables to track SpO₂, HRV, and recovery trends",
          "Hyperbaric oxygen therapy (HBOT) & other recommended strategies for cellular oxygen saturation and repair",
        ],
      },
    ],
  },
  8: {
    title: "Protocols for Cellular Performance",
    introText: [
      "Your PNOĒ results show how your cells are performing today. At SYNC, we turn those insights into action with a personalized Cellular Performance Protocol designed to restore metabolic flexibility, improve energy, and slow the biological clock.",
      "Here's how we translate your results into a personalized plan for stronger cellular health and longevity:",
    ],
    columns: [
      {
        title: "Testing & Tracking",
        items: [
          "Cellular performance insights that go beyond standard labs",
          "Hormone and nutrient testing to identify gaps",
          "Advanced toxin and genetic insights when deeper evaluation is needed",
          "Biological aging markers for long term tracking",
        ],
      },
      {
        title: "Targeted Therapies",
        items: [
          "Customized supplement plans to support mitochondria and reduce oxidative stress",
          "Personalized peptide and hormone support for cellular repair and energy",
          "Restorative nutrient IV therapies (NAD+, Glutathione, vitamin C)",
          "Targeted injections for fat metabolism and energy (B12, L-Carnitine)",
        ],
      },
      {
        title: "Lifestyle, \nTech & Nutrition Integrations",
        items: [
          "Exploring simple shifts in training (Zone 2, intervals) to boost mitochondrial capacity",
          "Nutrition strategies for metabolic flexibility and antioxidant support",
          "Recovery practices like sauna, cold therapy, and fasting for resilience",
          "Wearables to monitor recovery and metabolic adaptation",
          "HBOT to enhance cellular oxygenation and repair",
        ],
      },
    ],
  },
};

// Map ID 17 to use the same data as ID 5 (Lung Fitness)
syncLifePageData[17] = syncLifePageData[5];
// Map ID 10 to use the same data as ID 4 (Heart Fitness)
syncLifePageData[10] = syncLifePageData[4];
// Map ID 18 to use the same data as ID 25 (Stress Resiliency)
syncLifePageData[18] = syncLifePageData[25];

const SyncLifeCustomPage = ({ id }: { id: number }) => {
  const pageData = syncLifePageData[id];

  if (!pageData) {
    return (
      <Layout>
        <LogoWrapper>
          <img src={logo} alt="SYNC LIFE logo" style={{ maxWidth: "300px" }} />
        </LogoWrapper>
        <StyledTitle>Content not available for this metric type</StyledTitle>
      </Layout>
    );
  }

  return (
    <Layout>
      <LogoWrapper>
        <img src={logo} alt="SYNC LIFE logo" style={{ maxWidth: "550px" }} />
      </LogoWrapper>
      <StyledTitle>{pageData.title}</StyledTitle>
      <IntroSection>
        {pageData.introText.map((paragraph, index) => (
          <IntroParagraph key={index}>{paragraph}</IntroParagraph>
        ))}
      </IntroSection>

      <ColumnsContainer>
        {pageData.columns.map((column, columnIndex) => (
          <Column key={columnIndex}>
            <ColumnHeader>
              <img
                src={
                  columnIndex === 0
                    ? testingTrackingIcon
                    : columnIndex === 1
                    ? targetedTherapiesIcon
                    : lifestyleTechIcon
                }
                alt="category icon"
                style={{ width: "30px" }}
              />
              <ColumnTitle>
                {column.title.split("\n").map((line, lineIndex) => (
                  <span key={lineIndex}>
                    {line}
                    {lineIndex < column.title.split("\n").length - 1 && <br />}
                  </span>
                ))}
              </ColumnTitle>
            </ColumnHeader>
            <BulletList>
              {column.items.map((item, itemIndex) => (
                <BulletItem key={itemIndex}>{item}</BulletItem>
              ))}
            </BulletList>
          </Column>
        ))}
      </ColumnsContainer>
    </Layout>
  );
};

export default SyncLifeCustomPage;
